import { IconContext, type IconProps } from "@phosphor-icons/react";
import type { ReactNode } from "react";

/**
 * Shape of HealthOS-aware values that we set on {@link IconContext}.
 *
 * Phosphor's context uses `IconProps` directly; we alias it here so call sites
 * that reason about "icon defaults" don't have to know that implementation
 * detail.
 */
export type IconContextType = IconProps;

/**
 * HealthOS defaults for Phosphor icons.
 *
 * - `size={20}` matches our 5-unit (20px) glyph rhythm in `MicrostepCard`,
 *   `SideNav`, and attention surfaces.
 * - `weight="regular"` is the baseline; call sites switch to `"fill"` or
 *   `"duotone"` to signal emphasis (active nav, completion, streaks).
 * - `color="currentColor"` lets Tailwind drive colour via `text-*` classes so
 *   the icon inherits the semantic hue of its parent (teal for ambient,
 *   rose for rest, amber for attention, etc.).
 * - `mirrored={false}` is explicit for RTL correctness (we'll flip per-icon
 *   where it matters, not globally).
 */
export const HEALTHOS_ICON_DEFAULTS: IconContextType = {
  size: 20,
  weight: "regular",
  color: "currentColor",
  mirrored: false,
};

export interface IconProviderProps {
  children: ReactNode;
  /** Optional overrides, merged over {@link HEALTHOS_ICON_DEFAULTS}. */
  value?: Partial<IconContextType>;
}

/**
 * Wrap a tree to apply HealthOS's Phosphor defaults.
 *
 * Place once near the app root (inside `AppShell` or a surface that sets the
 * design-system context). Individual icons can still override any prop
 * locally (e.g. `<Moon weight="fill" size={24} />`).
 */
export function IconProvider({ children, value }: IconProviderProps) {
  return (
    <IconContext.Provider value={{ ...HEALTHOS_ICON_DEFAULTS, ...value }}>
      {children}
    </IconContext.Provider>
  );
}
